import { Drawer } from "../../components/ui/drawer";
import { Popover } from "../../components/ui/popover";
import { useControllableFlag } from "../../hooks/use-controllable-flag";
import { useOptionalAutoFocus } from "../../hooks/use-open-focus.ts";
import { useControllableState } from "../../hooks/use-controllable-state";
import { useIsMobile } from "../../hooks/use-media-query";
import * as React from "react";
import {
  EntitySelectorProvider,
  filterEntityItems,
  isItemCollapsed,
  type EntitySelectorConfig,
  type EntitySelectorContextValue,
  type EntitySelectorVariant,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntityBadgeConfig,
} from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

// One shared empty list, so "this pool has no items" is a stable reference and
// the effects keyed on it do not re-run every render.
const EMPTY_ITEMS: EntityItemConfig[] = [];

// Imperative handle for driving keyboard nav from outside the selector subtree
// (e.g. a TipTap mention menu forwarding arrows/Enter while focus stays in the editor).
export type EntitySelectorHandle = {
  move: (delta: number) => void;
  selectActive: () => void;
  getActiveId: () => string | null;
  /** Switch the active pool (tab) by offset, wrapping. No-op with <2 pools. */
  cycleActivePool: (delta: number) => void;
};

// Lead rows first inside their own section run (stable), so a row flagged
// `lead` heads the group it belongs to without leaving it. Sections keep
// the order they first appear in. With no lead the list is returned as
// given, so rows of one section split by another stay split; with a lead
// each section's rows gather into one run.
const leadFirst = (items: EntityItemConfig[]): EntityItemConfig[] => {
  if (!items.some((item) => item.lead)) return items;
  const sectionRank = new Map<string, number>();
  const rank = (item: EntityItemConfig) => {
    const key = `${item.section ?? ""}\u0000${item.subsection ?? ""}`;
    let at = sectionRank.get(key);
    if (at === undefined) {
      at = sectionRank.size;
      sectionRank.set(key, at);
    }
    return at;
  };
  for (const item of items) rank(item);
  return [...items].sort(
    (a, b) => rank(a) - rank(b) || Number(!a.lead) - Number(!b.lead),
  );
};

export type EntitySelectorRootProps = Omittable<{
  config: EntitySelectorConfig;
  value?: EntitySelection[];
  onValueChange?: (selections: EntitySelection[]) => void;
  defaultValue?: EntitySelection[];
  search?: string;
  onSearchChange?: (search: string) => void;
  /** Controlled open state. Pair with `onOpenChange` to fully drive the popover/drawer from outside. */
  open?: boolean;
  defaultOpen?: boolean;
  /** Notified whenever the popover/drawer opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /** The shell. Unset, a phone gets `drawer` — a bottom sheet whose search
   *  lifts it to the top under the keyboard — and every other tier
   *  `popover`; a popover with a search field inside a phone sheet has
   *  nowhere to go when the keyboard rises. An explicit value always wins:
   *  `inline` for a host that owns the popup, `popover` for a picker that
   *  must stay one on a phone. */
  variant?: EntitySelectorVariant;
  /** The phone sheet's caption: a small centred name for what the list is
   *  ("Assignees"), so a sheet of names under a search well says what it
   *  is. Set once per picker kind, on its wrapper. The popover shows nothing
   *  (its trigger is right there); `EntitySelectorContent title` overrides
   *  per content. */
  title?: React.ReactNode;
  /** Called before a selection is applied. Return `false` to veto — the popover closes but the displayed value stays unchanged. */
  onBeforeSelect?: (entity: EntityItemConfig, poolId: string) => boolean;
  ref?: React.Ref<EntitySelectorHandle>;
  children: React.ReactNode;
}>;

function EntitySelectorRoot({
  config,
  value: controlledValue,
  onValueChange,
  defaultValue,
  search: controlledSearch,
  onSearchChange,
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  variant: variantProp,
  title,
  onBeforeSelect,
  ref,
  children,
}: EntitySelectorRootProps) {
  const isMobile = useIsMobile();
  const variant: EntitySelectorVariant =
    variantProp ?? (isMobile ? "drawer" : "popover");
  const [selections, setSelections] = useControllableState<EntitySelection[]>({
    prop: controlledValue,
    defaultProp: defaultValue ?? [],
    onChange: onValueChange,
  });

  const [open, setOpenFlag] = useControllableFlag({
    prop: controlledOpen,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  const defaultPoolId = config.defaultPool ?? config.pools[0]?.id ?? "";
  const [activePool, setActivePool] = React.useState(defaultPoolId);
  const [search, setSearch] = useControllableState<string>({
    prop: controlledSearch,
    defaultProp: "",
    onChange: onSearchChange,
  });

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!next) setSearch("");
      setOpenFlag(next);
    },
    [setOpenFlag, setSearch],
  );

  const isInline = variant === "inline";
  const effectiveOpen = isInline ? true : open;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const focusInput = React.useCallback(() => {
    inputRef.current?.focus();
  }, []);
  // The drawer's search takes the caret on open only where no software
  // keyboard would rise with it (`useOptionalAutoFocus`: not on touch).
  const seatCaret = useOptionalAutoFocus();

  // A mounted `EntitySelectorSelected` lists the selections itself, so the
  // pools drop them — an entity appears once in the popup. Counted, not
  // flagged, so two sections (or StrictMode's mount/unmount/mount) cannot
  // leave the rule switched off while one is still on screen.
  const [selectedSectionCount, setSelectedSectionCount] = React.useState(0);
  const registerSelectedSection = React.useCallback(() => {
    setSelectedSectionCount((n) => n + 1);
    return () => setSelectedSectionCount((n) => n - 1);
  }, []);
  const hidesSelected = selectedSectionCount > 0;

  // Where each pool seats the selected section (see `registerSelectedHost`
  // on the context). Keyed by pool id so a tabbed selector seats it in the
  // pool that is showing.
  const [selectedHosts, setSelectedHosts] = React.useState<
    ReadonlyMap<string, HTMLElement>
  >(() => new Map());
  const registerSelectedHost = React.useCallback(
    (poolId: string, host: HTMLElement | null) => {
      setSelectedHosts((prev) => {
        if ((prev.get(poolId) ?? null) === host) return prev;
        const next = new Map(prev);
        if (host) next.set(poolId, host);
        else next.delete(poolId);
        return next;
      });
    },
    [],
  );
  const getSelectedHost = React.useCallback(
    (poolId: string) => selectedHosts.get(poolId) ?? null,
    [selectedHosts],
  );
  // The ids every pool list drops, or null when nothing is dropped — computed
  // once here because the pinned rows and the filtered rows both consult it.
  const omittedIds = React.useMemo(
    () =>
      hidesSelected && selections.length > 0
        ? new Set(selections.map((s) => s.id))
        : null,
    [hidesSelected, selections],
  );

  const toggleSelection = React.useCallback(
    (entity: EntityItemConfig, poolId: string) => {
      const exists = selections.find((s) => s.id === entity.id);
      // A disabled entity can be SHOWN as the current value but never newly
      // chosen, so an already-selected one still toggles off and the rest are
      // inert. Guarded here rather than on the row, because the keyboard path
      // (`selectActive`) never passes through a row's pointer handlers — leaving
      // Enter able to select what a click cannot.
      if (entity.disabled && !exists) return;

      // Allow callers to veto the selection (e.g. to open a confirmation dialog instead)
      if (onBeforeSelect && !onBeforeSelect(entity, poolId)) {
        setOpen(false);
        return;
      }

      if (config.maxSelections === 1) {
        // Single-select: replace (or keep) current selection and auto-close
        if (!exists) {
          setSelections([{ id: entity.id, poolId, entity }]);
        }
        setOpen(false);
        return;
      }
      if (exists) {
        setSelections(selections.filter((s) => s.id !== entity.id));
      } else if (
        config.maxSelections &&
        selections.length >= config.maxSelections
      ) {
        return;
      } else {
        setSelections([...selections, { id: entity.id, poolId, entity }]);
      }
    },
    [config.maxSelections, selections, setSelections, setOpen, onBeforeSelect],
  );

  const selectItems = React.useCallback(
    (entities: EntityItemConfig[], poolId: string) => {
      if (config.maxSelections === 1) return;
      const chosen = new Set(selections.map((s) => s.id));
      const additions: EntitySelection[] = [];
      for (const entity of entities) {
        if (chosen.has(entity.id) || entity.disabled) continue;
        if (onBeforeSelect && !onBeforeSelect(entity, poolId)) continue;
        chosen.add(entity.id);
        additions.push({ id: entity.id, poolId, entity });
      }
      if (additions.length === 0) return;
      const room = config.maxSelections
        ? Math.max(config.maxSelections - selections.length, 0)
        : additions.length;
      if (room === 0) return;
      setSelections([...selections, ...additions.slice(0, room)]);
    },
    [config.maxSelections, selections, setSelections, onBeforeSelect],
  );

  const deselectItems = React.useCallback(
    (entityIds: string[]) => {
      const dropped = new Set(entityIds);
      const kept = selections.filter((s) => !dropped.has(s.id));
      if (kept.length !== selections.length) setSelections(kept);
    },
    [selections, setSelections],
  );

  const isSelected = React.useCallback(
    (entityId: string) => {
      return selections.some((s) => s.id === entityId);
    },
    [selections],
  );

  const clearSelections = React.useCallback(() => {
    setSelections([]);
  }, [setSelections]);

  const getPool = React.useCallback(
    (poolId: string): EntityPoolConfig | undefined => {
      return config.pools.find((p) => p.id === poolId);
    },
    [config.pools],
  );

  const getPoolBadge = React.useCallback(
    (poolId: string): EntityBadgeConfig | undefined => {
      return getPool(poolId)?.itemBadge;
    },
    [getPool],
  );

  // Notify pools when the search query changes so they can fetch matching items from the server.
  // Use a ref for pools to avoid re-triggering when pool objects update after search results
  // arrive (which would cause an infinite fetch loop: search → new items → new pool ref → re-fire effect).
  const poolsRef = React.useRef(config.pools);
  poolsRef.current = config.pools;

  React.useEffect(() => {
    for (const pool of poolsRef.current) {
      pool.onSearchChange?.(search);
    }
  }, [search]);

  const searchRef = React.useRef(search);
  searchRef.current = search;
  React.useEffect(
    () => () => {
      if (!searchRef.current) return;
      for (const pool of poolsRef.current) pool.onSearchChange?.("");
    },
    [],
  );

  // Per-pool filter cache keyed on (pool.items ref, search) so a pool
  // whose items are unchanged isn't re-scanned when another pool's hook emits a
  // new items ref. serverFiltered pools skip client filtering entirely.
  const poolFilterCacheRef = React.useRef(
    new Map<
      string,
      { items: EntityItemConfig[]; search: string; filtered: EntityItemConfig[] }
    >(),
  );

  const matchedItemsCache = React.useMemo(() => {
    const cache = new Map<string, EntityItemConfig[]>();
    const prev = poolFilterCacheRef.current;
    const next = new Map<
      string,
      { items: EntityItemConfig[]; search: string; filtered: EntityItemConfig[] }
    >();
    for (const pool of config.pools) {
      const entry = prev.get(pool.id);
      let filtered: EntityItemConfig[];
      if (
        entry &&
        entry.items === pool.items &&
        entry.search === search
      ) {
        filtered = entry.filtered;
      } else if (pool.serverFiltered) {
        filtered = leadFirst(pool.items);
      } else {
        filtered = leadFirst(filterEntityItems(pool.items, search));
      }
      next.set(pool.id, {
        items: pool.items,
        search,
        filtered,
      });
      cache.set(pool.id, filtered);
    }
    poolFilterCacheRef.current = next;
    return cache;
  }, [config.pools, search]);

  // The search matches minus what the selected section lists (see
  // `registerSelectedSection` on the context). Applied HERE, not in the pool renderer,
  // because this is the list the keyboard-nav order is built from below: the
  // cursor must never be able to land on a row the pool did not draw. The
  // per-pool omitted counts ride along for the pool's virtual `total` and the
  // empty state.
  const { filteredItemsCache, omittedCountCache } = React.useMemo(() => {
    const items = new Map<string, EntityItemConfig[]>();
    const omitted = new Map<string, number>();
    for (const pool of config.pools) {
      const matched = matchedItemsCache.get(pool.id) ?? EMPTY_ITEMS;
      const kept = omittedIds
        ? matched.filter((item) => !omittedIds.has(item.id))
        : matched;
      // Keep the matched reference when nothing was dropped, so a consumer
      // memoized on the list does not churn on every selection change.
      items.set(pool.id, kept.length === matched.length ? matched : kept);
      omitted.set(pool.id, matched.length - kept.length);
    }
    return { filteredItemsCache: items, omittedCountCache: omitted };
  }, [config.pools, matchedItemsCache, omittedIds]);

  const getFilteredItems = React.useCallback(
    (poolId: string): EntityItemConfig[] =>
      filteredItemsCache.get(poolId) ?? [],
    [filteredItemsCache],
  );

  const getOmittedCount = React.useCallback(
    (poolId: string): number => omittedCountCache.get(poolId) ?? 0,
    [omittedCountCache],
  );

  // Pinned rows, search-filtered per pool. Computed HERE (not in the pool
  // renderer) because they participate in keyboard navigation: they render
  // above the list, so the nav list is pinned + filtered and pinned rows own
  // indices 0..n-1. Keeping the pool's render source and the nav list on the
  // same memo means the highlight can never point at a row the pool isn't
  // showing.
  const pinnedItemsCache = React.useMemo(() => {
    const cache = new Map<string, EntityItemConfig[]>();
    for (const pool of config.pools) {
      const matched = pool.pinnedItems?.length
        ? filterEntityItems(pool.pinnedItems, search)
        : EMPTY_ITEMS;
      cache.set(
        pool.id,
        omittedIds
          ? matched.filter((item) => !omittedIds.has(item.id))
          : matched,
      );
    }
    return cache;
  }, [config.pools, search, omittedIds]);

  const getPinnedItems = React.useCallback(
    (poolId: string): EntityItemConfig[] => pinnedItemsCache.get(poolId) ?? [],
    [pinnedItemsCache],
  );

  // ── Collapsed sections ──────────────────────────────────────────────────
  // Held HERE rather than in the pool renderer for the same reason pinned rows
  // are: a folded section's rows are dropped from the nav list below, and only
  // one owner can keep "what is rendered" and "what the cursor can reach" in
  // agreement. Keyed per pool, so two pools sharing a section label fold
  // independently. Browse and search keep separate sets (see the context docs).
  const defaultCollapsed = React.useMemo(() => {
    const byPool = new Map<string, string[]>();
    for (const pool of config.pools) {
      if (pool.defaultCollapsedSections?.length)
        byPool.set(pool.id, pool.defaultCollapsedSections);
    }
    return byPool;
  }, [config.pools]);

  const [collapsedSections, setCollapsedSections] = React.useState<
    Map<string, Set<string>>
  >(() => new Map([...defaultCollapsed].map(([id, keys]) => [id, new Set(keys)])));
  const [searchCollapsed, setSearchCollapsed] = React.useState<
    Map<string, Set<string>>
  >(() => new Map());

  // Each default label is applied exactly once. Seeding the state above and
  // stopping there ignored a collapse set that only exists after an async query
  // resolves — the automation pickers derive their provider sections from one, so
  // a dropdown opened on a cold cache came up fully expanded. Re-applying the
  // whole set whenever it changes would instead re-fold a section the user just
  // opened, so the applied labels are remembered and only new ones are folded.
  const appliedDefaultsRef = React.useRef<Map<string, Set<string>> | null>(null);
  React.useEffect(() => {
    const applied = (appliedDefaultsRef.current ??= new Map(
      [...defaultCollapsed].map(([id, keys]) => [id, new Set(keys)]),
    ));
    const fresh = new Map<string, string[]>();
    for (const [poolId, keys] of defaultCollapsed) {
      const seen = applied.get(poolId);
      const added = seen ? keys.filter((key) => !seen.has(key)) : keys;
      if (added.length) fresh.set(poolId, added);
    }
    if (fresh.size === 0) return;
    for (const [poolId, keys] of fresh) {
      const seen = applied.get(poolId) ?? new Set<string>();
      for (const key of keys) seen.add(key);
      applied.set(poolId, seen);
    }
    setCollapsedSections((prev) => {
      const next = new Map(prev);
      for (const [poolId, keys] of fresh) {
        const set = new Set(next.get(poolId) ?? []);
        for (const key of keys) set.add(key);
        next.set(poolId, set);
      }
      return next;
    });
  }, [defaultCollapsed]);

  const isSearching = search.trim().length > 0;
  React.useEffect(() => {
    if (!isSearching) {
      setSearchCollapsed((prev) => (prev.size ? new Map() : prev));
    }
  }, [isSearching]);

  const activeCollapsed = isSearching ? searchCollapsed : collapsedSections;
  const isSectionCollapsed = React.useCallback(
    (poolId: string, key: string) => !!activeCollapsed.get(poolId)?.has(key),
    [activeCollapsed],
  );
  const toggleSection = React.useCallback(
    (poolId: string, key: string) => {
      const setState = isSearching ? setSearchCollapsed : setCollapsedSections;
      setState((prev) => {
        const next = new Map(prev);
        const set = new Set(next.get(poolId) ?? []);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        next.set(poolId, set);
        return next;
      });
    },
    [isSearching],
  );

  // The keyboard-nav list: pinned rows first, then the regular filtered list —
  // matching visual order in the pool.
  const navItemsCache = React.useMemo(() => {
    const cache = new Map<string, EntityItemConfig[]>();
    for (const pool of config.pools) {
      // Both caches are keyed by this same pool list, so every id is present.
      const pinned = pinnedItemsCache.get(pool.id)!;
      const filtered = filteredItemsCache.get(pool.id)!;
      // Rows under a folded heading are not rendered, so they are not navigable
      // either.
      const closed = pool.collapsibleSections
        ? activeCollapsed.get(pool.id)
        : undefined;
      const visible = closed?.size
        ? filtered.filter((item) => !isItemCollapsed(item, (key) => closed.has(key)))
        : filtered;
      cache.set(pool.id, pinned.length ? [...pinned, ...visible] : visible);
    }
    return cache;
  }, [config.pools, pinnedItemsCache, filteredItemsCache, activeCollapsed]);

  // Memoized because effects below depend on it: the `?? []` fallback mints a
  // fresh array every render, which would re-run them on every render.
  const activeItems = React.useMemo(
    () => navItemsCache.get(activePool) ?? EMPTY_ITEMS,
    [navItemsCache, activePool],
  );
  const [activeIndex, setActiveIndex] = React.useState(0);
  // The cursor paints only after the keyboard drives it (see the context type).
  const [cursorVisible, setCursorVisible] = React.useState(false);

  // Which input last moved the cursor. Only the pointer path sets this true, and
  // every other path clears it, so the pool can scroll the active row into view
  // for the keyboard and leave the list alone for hover. See
  // `isActiveFromPointer` on the context for why that distinction matters.
  const activeFromPointerRef = React.useRef(false);
  // The row the open seated the cursor on, held until the user moves it. While
  // seated, the cursor follows that ROW, not an index: the nav list changes
  // under it right after the open — a selected section registers a paint late
  // (layout effect) and lifts its rows out of the pools — and an index seated
  // on the selection would then name whichever stranger slid into that slot,
  // and the pool would scroll to it, opening the list part-way down. A seated
  // row that leaves the list puts the cursor back on row 0, in the same render.
  const [cursorSeat, setCursorSeat] = React.useState<string | null>(null);
  const activateFromPointer = React.useCallback((index: number) => {
    activeFromPointerRef.current = true;
    setCursorSeat(null);
    setActiveIndex(index);
  }, []);
  const isActiveFromPointer = React.useCallback(
    () => activeFromPointerRef.current,
    [],
  );

  // Reset highlight on query/pool change. Items arriving async (same query) keep
  // index 0 and are clamped at use. A live query paints the cursor on the first
  // match — that highlight is what tells the user Enter will pick it; clearing
  // the query hides the cursor again until the keyboard asks for it.
  React.useEffect(() => {
    activeFromPointerRef.current = false;
    setCursorSeat(null);
    setActiveIndex(0);
    setCursorVisible(search.trim().length > 0);
  }, [search, activePool]);

  // Open with the cursor on the current selection (combobox convention) —
  // otherwise row 0 carries the keyboard highlight while the checked row
  // carries the selection highlight, reading as two highlighted items. The ref
  // gates this to once per open so it still fires when pool items arrive async
  // after the popover mounts, but never yanks the cursor afterwards. A search
  // typed before items arrive consumes the gate: the first-match reset wins.
  const cursorInitRef = React.useRef(false);
  React.useEffect(() => {
    if (!effectiveOpen) {
      cursorInitRef.current = false;
      setCursorSeat(null);
      setCursorVisible(false);
      return;
    }
    if (cursorInitRef.current) return;
    if (search) {
      cursorInitRef.current = true;
      return;
    }
    if (activeItems.length === 0) return;
    cursorInitRef.current = true;
    const idx = activeItems.findIndex((item) =>
      selections.some((s) => s.id === item.id),
    );
    // Opening ON the selection is meant to scroll it into view, so this is not
    // a pointer activation.
    activeFromPointerRef.current = false;
    setCursorSeat(idx >= 0 ? (activeItems[idx]?.id ?? null) : null);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [effectiveOpen, search, activeItems, selections]);

  const seatedIndex =
    cursorSeat === null
      ? -1
      : activeItems.findIndex((item) => item.id === cursorSeat);
  const clampedActiveIndex =
    activeItems.length === 0
      ? 0
      : cursorSeat !== null
        ? Math.max(seatedIndex, 0)
        : Math.min(activeIndex, activeItems.length - 1);
  const activeId = activeItems[clampedActiveIndex]?.id ?? null;

  const moveActive = React.useCallback(
    (delta: number) => {
      const n = activeItems.length;
      if (n === 0) return;
      activeFromPointerRef.current = false;
      setCursorSeat(null);
      setCursorVisible(true);
      setActiveIndex((((clampedActiveIndex + delta) % n) + n) % n);
    },
    [activeItems, clampedActiveIndex],
  );

  const selectActive = React.useCallback(() => {
    // Enter acts only on a PAINTED cursor. With the cursor hidden there is no
    // visible target, and toggling one anyway silently flips whichever row the
    // hidden index happens to rest on.
    if (!cursorVisible) return;
    const item = activeItems[clampedActiveIndex];
    if (item) toggleSelection(item, activePool);
  }, [cursorVisible, activeItems, activePool, clampedActiveIndex, toggleSelection]);

  // A pool switch seats the caret in the search well, so the next keystroke
  // is a query over the pool that just opened. Before the state update, not
  // only a frame after it: the control that asked may leave the DOM with the
  // switch (a step's footer verb, its back chevron), and a focused element
  // that unmounts hands focus to the popup — which then outranks the
  // frame-later focus. Only where a caret is welcome (`seatCaret`): on a
  // coarse pointer the well waits for its own tap, so a switch raises no
  // software keyboard over the list it just showed.
  const handleSetActivePool = React.useCallback(
    (poolId: string) => {
      if (seatCaret) focusInput();
      setActivePool(poolId);
      requestAnimationFrame(() => {
        if (seatCaret) focusInput();
      });
    },
    [focusInput, seatCaret],
  );

  const contextValue: EntitySelectorContextValue = React.useMemo(
    () => ({
      config,
      variant,
      title,
      selections,
      toggleSelection,
      selectItems,
      deselectItems,
      isSelected,
      clearSelections,
      activePool,
      setActivePool: handleSetActivePool,
      search,
      setSearch,
      open: effectiveOpen,
      onOpenChange: setOpen,
      getPool,
      getPoolBadge,
      getFilteredItems,
      getPinnedItems,
      registerSelectedSection,
      registerSelectedHost,
      getSelectedHost,
      getOmittedCount,
      isSectionCollapsed,
      toggleSection,
      activeIndex: clampedActiveIndex,
      activeId,
      cursorVisible,
      setActiveIndex: activateFromPointer,
      isActiveFromPointer,
      moveActive,
      selectActive,
      inputRef,
      focusInput,
    }),
    [
      config,
      variant,
      title,
      selections,
      toggleSelection,
      selectItems,
      deselectItems,
      isSelected,
      clearSelections,
      activePool,
      handleSetActivePool,
      search,
      setSearch,
      effectiveOpen,
      setOpen,
      getPool,
      getPoolBadge,
      getFilteredItems,
      getPinnedItems,
      registerSelectedSection,
      registerSelectedHost,
      getSelectedHost,
      getOmittedCount,
      isSectionCollapsed,
      toggleSection,
      clampedActiveIndex,
      activeId,
      cursorVisible,
      activateFromPointer,
      isActiveFromPointer,
      moveActive,
      selectActive,
      focusInput,
    ],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      move: moveActive,
      selectActive,
      getActiveId: () => activeId,
      cycleActivePool: (delta: number) => {
        const pools = config.pools;
        if (pools.length < 2) return;
        const idx = Math.max(
          0,
          pools.findIndex((p) => p.id === activePool),
        );
        const next =
          pools[(((idx + delta) % pools.length) + pools.length) % pools.length];
        if (next) handleSetActivePool(next.id);
      },
    }),
    [
      moveActive,
      selectActive,
      activeId,
      config.pools,
      activePool,
      handleSetActivePool,
    ],
  );

  if (isInline) {
    return (
      <EntitySelectorProvider value={contextValue}>
        {children}
      </EntitySelectorProvider>
    );
  }

  if (variant === "drawer") {
    return (
      <Drawer
        open={open}
        onOpenChange={(next) => setOpen(next)}
        // A `half` drawer: a finger's open rests at the middle with no
        // keyboard, and a tap on the search lifts it to the top. Where the
        // search does take the caret, it waits for the open animation to
        // settle — focusing mid-animation opens the mobile virtual keyboard,
        // which resizes the visual viewport and desyncs the drawer's
        // translate math (a top clip, list scroll locked until a reflow).
        onOpenChangeComplete={(next) => {
          if (next && seatCaret) focusInput();
        }}
      >
        <EntitySelectorProvider value={contextValue}>
          {children}
        </EntitySelectorProvider>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={(next) => setOpen(next)}>
      <EntitySelectorProvider value={contextValue}>
        {children}
      </EntitySelectorProvider>
    </Popover>
  );
}

EntitySelectorRoot.displayName = "EntitySelector";

export { EntitySelectorRoot };
